import { Button, CircularProgress, Grid } from '@material-ui/core';
import { BackIcon } from '@room-match/shared-ui-components';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { RESUME_DEFAULT_DATA } from 'shared/constants/INITIAL_RESUME';
import { ROUTES } from 'shared/constants/ROUTES';
import { IAddress } from 'shared/interfaces/IAddress';
import { IGeneralSection } from 'shared/interfaces/IGeneral';
import { IProfileMediaSetSelectPayload } from 'shared/interfaces/IProfile';
import { ITalentUpdatePayload } from 'shared/interfaces/ITalent';
import { IAgency } from 'shared/interfaces/utils/IAgency';
import { setUser } from 'shared/redux/slicers/user.slicer';
import { useAppSelector } from 'shared/redux/store';
import { locationService } from 'shared/services/locationService';
import { profileService } from 'shared/services/profileService';
import { talentService } from 'shared/services/talentService';
import { uploadService } from 'shared/services/uploadService';
import { ls } from 'shared/utils/ls';
import { errorResponseToArray } from 'shared/utils/room-shared';
import { useDebounce } from 'use-debounce';
import { captureMessage } from '../../../lib/sentry';
import { useStyles } from '../RegistrationForm.styles';
import { PersonalInformation } from './PersonalInformation';
import { ProfileImageAdult } from './ProfileImageAdult';
import { useAdultForm } from './useAdultForm';

const { setSelectProfileMedia, setProfilePrimaryImage, getProfileMatch, createMultipleProfiles, claimGhostedProfile } =
  profileService();
const { updateMe, updateTalent } = talentService();
const { uploadMedia } = uploadService();
const { createLocation } = locationService();
interface Props {
  showError: (header: string, message: string, button_text: string) => void;
}

const AdultForm: React.FC<Props> = ({ showError }) => {
  const { mutateAsync: createLocAsync } = createLocation();
  const { mutateAsync: updateTalentResume } = updateTalent();
  const { mutateAsync } = updateMe();
  const { mutateAsync: mutateAsyncUploadMedia } = uploadMedia();
  const { mutateAsync: claimAsync } = claimGhostedProfile();
  const { mutateAsync: mutateAsyncCreateMulitipleProfile } = createMultipleProfiles();
  const { mutate: primaryImageMutate } = setProfilePrimaryImage();
  const { mutateAsync: bindImageAsync } = setSelectProfileMedia();
  const dispatch = useDispatch();
  const { user: userState } = useAppSelector((state) => state);

  const [selectedLocation, setSelectedLocation] = useState<IAddress | null>(null);

  const { getLS, setLS } = ls();
  const { body, footer, backIconButton, buttonCls } = useStyles();
  const { resetToken, profileId, name, id } = JSON.parse(getLS('agency') || '{}');
  const { step } = useParams() as { step: string };
  const projectId = getLS('projectId');
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [val, setVal] = useState<number[] | null>([15, 23]);

  const [generalInfo, setgeneralInfo] = useState<IGeneralSection>({
    ethnic_appearance_primary: '',
    ethnic_appearance_secondary: '',
    ethnic_appearance_other: '',
    hair_colour: '',
    eye_colour: '',
    hair_length: '',
    tattoo: [],
    piercings: [],
    facial_hair: [],
    physical_attributes: [],
    twin_triplet: [],
  });

  const [debounceFirst] = useDebounce(firstName, 500);
  const [debounceLast] = useDebounce(lastName, 500);

  const { data: profileMatchData } = getProfileMatch(true, debounceFirst, debounceLast, projectId);
  const [representatives, setRepresentatives] = useState<
    {
      id: string;
      type: string;
      attributes: IAgency;
    }[]
  >([]);

  // prettier-ignore
  const [uploadedImg, setUploadedImg] = useState<{
    name: string;
    type: string;
    size: string;
    file: Blob | undefined;
    imageAttr: { width: number; height: number };
  } | null>(null);

  const history = useHistory();

  // useEffect(() => {
  //   captureMessage('user landed on adult registration page');
  // }, []);

  const handleSubmit = async (values: ITalentUpdatePayload) => {
    const modifiedValue = { ...values, completed_primary_details: false };
    delete modifiedValue.representation;
    setIsLoading(true);
    // captureMessage({
    //   message: 'user submitted form with values',
    //   data: modifiedValue,
    // });
    if (selectedLocation)
      await createLocAsync({
        ...selectedLocation?.attributes,
      });

    await mutateAsync(
      {
        ...modifiedValue,
      },
      {
        onSuccess: async (user) => {
          setLS('user', JSON.stringify(user));
          setIsLoading(false);
          history.push(ROUTES.REGISTRATION + '/adult/profile_image');
        },
        onError(error: any) {
          captureMessage({
            message: 'user registration failed with error',
            data: error?.response,
            level: 'error',
          });
          const errorMessage = error.response.data.errors.base[0];
          showError('Registration Failed', errorMessage, 'OK');
          setIsLoading(false);
        },
      },
    );
  };

  const { form } = useAdultForm({
    onSubmit: handleSubmit,
  });

  const adultButtonValidation = () => {
    if (form.values.country_code?.toLowerCase() === 'us' && !form.values.unions?.length) return true;
    if (!selectedLocation) return true;
    if (!generalInfo.ethnic_appearance_primary) return true;
    if ((form.values.other_phone_numbers || [])[0].number.length <= 10) return true;
    if (!form.values.birth_date) return true;
    if (representatives.length || form.values.representation === 'agency_representation') {
      if (!representatives.length) return true;
      //check each representatives if required_access_code is true check if has access_code has value
      const hasAccessCode = representatives.some((r) => {
        if (r.attributes.required_access_code) {
          return r.attributes.access_code && r.attributes.access_code.length === 0;
        }
        return false;
      });

      const divisionSupplied = representatives.some((r) => {
        if (r.attributes.divisions?.length > 0) {
          return r.attributes.selected_divisions ? r.attributes.selected_divisions?.length === 0 : true;
        }
        return false;
      });

      return divisionSupplied || hasAccessCode || !representatives.length || !!Object.values(form.errors).length;
    } else {
      return !!Object.values(form.errors).length;
    }
  };

  const navigateToSuccessPage = (medium_id: string) => {
    // captureMessage(`user created profile successfully, navigating to success page`);
    mutateAsync(
      {
        medium_id,
        completed_primary_details: true,
      },
      {
        onSuccess: async (user) => {
          setLS('user', JSON.stringify(user));
          await updateTalentResume({
            general: generalInfo,
            resume: RESUME_DEFAULT_DATA,
            age_range_from: val ? val[0] : 15,
            age_range_to: val ? val[1] : 23,
          });
          setIsLoading(false);
          if (userState.user) {
            dispatch(
              setUser({
                user: {
                  ...userState.user,
                  attributes: {
                    ...userState.user?.attributes,
                    picture: user.data.attributes.picture || '',
                    first_name: user.data.attributes.first_name || '',
                    last_name: user.data.attributes.last_name || '',
                    stage_name: user.data.attributes.stage_name || '',
                    stage_first_name: user.data.attributes.stage_first_name || '',
                    stage_last_name: user.data.attributes.stage_last_name || '',
                  },
                },
              }),
            );
          }
          history.push(ROUTES.REGISTRATION + '/complete');
        },
        onError(error: any) {
          captureMessage({
            message: 'user failed to update record with error',
            data: error?.response,
            level: 'error',
          });
          const errorMessage = error.response.data.errors.base[0];
          showError('Registration Failed', errorMessage, 'OK');
          setIsLoading(false);
        },
      },
    );
  };

  const handleUploadImg = async (
    name: string,
    type: string,
    size: string,
    file?: Blob,
    imageAttr?: { width: number; height: number },
  ) => {
    if (file && imageAttr) {
      setUploadedImg({ name, type, size, file, imageAttr });
    }
  };

  const handleMakePrimary = (mediaId: string) => {
    const formData = new FormData();
    formData.set('medium_id', mediaId);

    primaryImageMutate({ profileId, formData });
  };

  const bindToProfile = async (payload: IProfileMediaSetSelectPayload) => {
    await bindImageAsync(
      { profileId, payload: { ...payload, sort: 0 } },
      {
        onError: (errors) => {
          if (errors?.response?.data?.errors) {
            const errorResponseArray: any = errorResponseToArray(errors.response.data.errors);
            console.error('error', errorResponseArray.join(','));
          } else {
            console.error('error', 'Something went wrong');
          }
        },
      },
    );
    handleMakePrimary(payload.id);
  };

  const AddRepresentatives = async (medium_id: string) => {
    //removing !resetToken since it will overwrite the exisiting ghosted profile on division creation
    // if (profileMatchData?.data.length && !resetToken) {
    if (profileMatchData?.data.length) {
      // captureMessage(`user has existing profile, claiming ghosted profile`);
      await claimAsync(
        medium_id
          ? { profileId: profileMatchData.data.map((p) => p.profile_id), medium_id }
          : { profileId: profileMatchData.data.map((p) => p.profile_id) },
        {
          onSuccess: async () => {
            // captureMessage(`user claimed ghosted profile successfully `);
          },
          onError: (error: any) => {
            captureMessage({
              message: 'user failed to claim ghosted profile with error',
              data: error?.response,
              level: 'error',
            });
            showError(
              'Registration Failed',
              error?.response?.data?.errors?.base[0] || 'Failed to process request',
              'OK',
            );
          },
        },
      );
    }

    if (representatives.length || (name && id)) {
      // captureMessage({
      //   message: 'user has representatives, creating profile with representatives',
      //   data: representatives,
      // });
      if (resetToken) {
        //====================================================================//
        //TODO: WILL NOT CREATE PROFILE AT THIS POINT ADD LOG HERE IMPORTANT!!//
        //====================================================================//
        //set uploaded image as profile primary image
        // remove this statement, this is for binding the image to profile which will be override by line 217
        // bindToProfile({
        //   id: medium_id,
        //   tag_list: 'image',
        //   media_tag_list: [''],
        // });
        // overriding the return statement to accept division creation | filter representatives that has division
        // return;
      }
      // const profilesArr: any = name ? [{ agency_id: id, representation_type: 'Agency Representation' }] : [];
      const profilesArr: any = [];

      //filter representatives that has division if profileMatchData is not empty
      const filteredRepresentatives = representatives.filter((r) => {
        if (profileMatchData?.data.length) {
          return (
            r.attributes.divisions?.length &&
            r.attributes.selected_divisions?.length > 0 &&
            r.attributes.selected_divisions[0] !== 'any'
          );
        }
        return true;
      });

      filteredRepresentatives.map((r) => {
        // profilesArr.push({ agency_id: r.id as string, representation_type: 'Agency Representation' });
        profilesArr.push({
          agency_id: r.id as string,
          representation_type: 'Agency Representation',
          ...(r.attributes.divisions.length > 0 &&
            r.attributes.selected_divisions[0] !== 'any' && {
              division_ids: r.attributes.selected_divisions,
            }),
        });
      });

      if (profilesArr.length === 0) {
        // captureMessage(`user has no representatives, creating profile with representation type Non-Represented`);
        return navigateToSuccessPage(medium_id);
      }
      mutateAsyncCreateMulitipleProfile(
        { profiles: [...profilesArr], medium_id },
        {
          onSuccess: () => navigateToSuccessPage(medium_id),
          onError: async (error: any) => {
            if (error?.response?.data?.errors.agency) {
              captureMessage({
                message: 'user bypass with error',
                data: error?.response,
                level: 'error',
              });
              //added here to bin image to exisiting profile
              if (profileId)
                await bindToProfile({
                  id: medium_id,
                  tag_list: 'image',
                  media_tag_list: [''],
                });
              return navigateToSuccessPage(medium_id);
            }
            setIsLoading(false);
            captureMessage({
              message: 'user failed to create profile with error',
              data: error?.response,
              level: 'error',
            });
            showError(
              'Registration Failed',
              JSON.stringify((error?.response?.data?.errors.agency || [])[0] || 'Failed to process request'),
              'OK',
            );
          },
        },
      );
    } else {
      //talent is a casting private talent
      if (!id && profileId) {
        // captureMessage(`user is a casting private talent, skipping profile creation`);
        await bindToProfile({
          id: medium_id,
          tag_list: 'image',
          media_tag_list: [''],
        });
        return navigateToSuccessPage(medium_id);
      }

      // captureMessage(`user has no representation, creating profile with representation type Non-Represented`);
      mutateAsyncCreateMulitipleProfile(
        {
          profiles: [
            {
              representation_type: 'Non-Represented',
            },
          ],
          medium_id,
        },
        {
          onSuccess: () => {
            // captureMessage(`user created profile successfully, navigating to success page`);
            navigateToSuccessPage(medium_id);
          },
          onError: (error: any) => {
            setIsLoading(false);
            captureMessage({
              message: 'Profile creation error',
              data: error?.response,
              level: 'error',
            });
          },
        },
      );
    }
  };

  const handleUploadAndProceed = async () => {
    if (uploadedImg) {
      if (uploadedImg.file) {
        setIsLoading(true);
        const formData = new FormData();
        formData.append('attachment', uploadedImg.file);
        formData.append('file_name', uploadedImg.name);
        formData.append('file_width', uploadedImg.imageAttr.width.toString());
        formData.append('file_height', uploadedImg.imageAttr.height.toString());
        formData.append('file_size', uploadedImg.size);
        formData.append('file_type', uploadedImg.type);
        formData.append('media_bin', 'true');
        const object: any = {};
        formData.forEach((value, key) => (object[key] = value));
        // captureMessage({
        //   message: 'user uploaded the following image',
        //   data: object,
        // });
        mutateAsyncUploadMedia(
          { formData },
          {
            onSuccess: async (data) => {
              AddRepresentatives(data.data.id);
              // captureMessage(`user uploaded image successfully proceeding to next step`);
            },
            onError(error: any) {
              captureMessage({
                message: 'user image upload failed with error',
                data: error?.response,
                level: 'error',
              });
              const errorMessage = error?.response?.data?.errors?.base[0];
              showError(
                'Image Upload Failed',
                errorMessage
                  ? `${errorMessage}\n\n Please choose another image and try again.`
                  : 'Please choose another image and try again.',
                'OK',
              );
              setIsLoading(false);
            },
          },
        );
      }
    }
  };

  const renderComponent = () => {
    switch (step) {
      case 'profile_image':
        return (
          <Grid item className={body}>
            <ProfileImageAdult onFileSelected={handleUploadImg} />
            <Grid item className={footer}>
              <Button className={backIconButton} onClick={() => history.push(ROUTES.REGISTRATION + '/adult')}>
                <BackIcon htmlColor="lightgrey" />
              </Button>
              <Button
                color="primary"
                variant="contained"
                disableElevation
                fullWidth
                className={buttonCls}
                onClick={handleUploadAndProceed}
                disabled={!uploadedImg || isLoading}
              >
                {isLoading ? <CircularProgress size={25} /> : 'Continue'}
              </Button>
            </Grid>
          </Grid>
        );

      default:
        return (
          <Grid item className={body}>
            <PersonalInformation
              val={val}
              setVal={setVal}
              selectedLocation={selectedLocation}
              setSelectedLocation={setSelectedLocation}
              profileMatch={profileMatchData}
              setFirstName={setFirstName}
              setLastName={setLastName}
              form={form}
              setRepresentatives={setRepresentatives}
              representatives={representatives}
              generalInfo={generalInfo}
              setgeneralInfo={setgeneralInfo}
            />
            <Grid item className={footer}>
              <Button className={backIconButton} onClick={() => history.push(ROUTES.REGISTRATION)}>
                <BackIcon htmlColor="lightgrey" />
              </Button>
              <Button
                color="primary"
                variant="contained"
                disableElevation
                fullWidth
                className={buttonCls}
                onClick={() => form.handleSubmit()}
                disabled={adultButtonValidation() || isLoading}
              >
                {isLoading ? <CircularProgress size={25} /> : 'Continue'}
              </Button>
            </Grid>
          </Grid>
        );
    }
  };

  return renderComponent();
};

export default AdultForm;
